/**
 * @fileOverview This file contains the implementation of the warehouse datagrid for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

//#region imports
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';

import customAxios from '../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { GlobalVariables } from '../../globalVar';
import { useTableLocale } from '../../hooks/useTableLocale';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
//#endregion

export const WarehouseTable = () => {
  const [warehouseData, setWarehouseData] = useState([]);
  const locale = useTableLocale();

  const {
    inventoryWarehouseStr,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
  } = useTranslationHook();

  const endpoint = `${GlobalVariables.getURL()}items`;

  const getData = async () => {
    await customAxios
      .get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      })
      .then((response) => {
        const warehouseData = response.data.data;
        setWarehouseData(warehouseData);
      });
  };

  // Columns
  const warehouseColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'code',
      headerName: 'Código',
      width: 100,
    },
    {
      field: 'name',
      headerName: 'Nombre',
      width: 200,
    },
    {
      field: 'model',
      headerName: 'Modelo',
      width: 100,
    },
    {
      field: 'manufacturer_name',
      headerName: 'Fabricante',
      width: 100,
    },
    {
      field: 'priority',
      headerName: 'Prioridad',
      width: 100,
      renderCell: (params) => {
        const p = params.row.priority;

        if (p === 0) {
          return (
            <Box
              display="flex"
              color="#f8453b"
              bgcolor="#F8463B4e"
              paddingX={2}
              borderRadius={5}
            >
              <Typography>{params.row.priority}</Typography>
            </Box>
          );
        } else if (p === 1) {
          return (
            <Box
              display="flex"
              color="#fc832d"
              bgcolor="#fc832d4e"
              paddingX={2}
              borderRadius={5}
            >
              <Typography>{params.row.priority}</Typography>
            </Box>
          );
        } else {
          return (
            <Box
              display="flex"
              color="#c9a628"
              bgcolor="#ffd9504e"
              paddingX={2}
              borderRadius={5}
            >
              <Typography>{params.row.priority}</Typography>
            </Box>
          );
        }
      },
    },
    {
      field: 'currency_name',
      headerName: 'Divisa',
      width: 100,
    },
    {
      field: 'cost',
      headerName: 'Costo',
      width: 100,
      renderCell: (params) => {
        const cost = params.row.cost.toFixed(2);
        return cost;
      },
    },
    {
      field: 'min_stock',
      headerName: 'Mín. Stock',
      width: 100,
    },
    {
      field: 'max_stock',
      headerName: 'Máx. Stock',
      width: 100,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 300,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: 150,
    },
    {
      field: 'created_by_name',
      headerName: createdByString,
      width: 150,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: 150,
    },
    {
      field: 'updated_by_name',
      headerName: updatedByString,
      width: 150,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">{inventoryWarehouseStr} </span>
        </div>
      </div>

      <DataGridPro
        pagination
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        rows={warehouseData}
        columns={warehouseColumns}
        disableSelectionOnClick
        slots={{ toolbar: GridToolbar }}
      />
    </>
  );
};
